import { FC, useState } from "react";

import { formatDollars } from "capitalrx-utilities";

import { Button, Buttons, Icon, Menu } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";

import styles from "./BenefitSearchForm.module.scss";

export interface AccumulationsData {
  data: {
    family_accumulated: string;
    family_maximum: string;
    individual_accumulated: string;
    individual_maximum: string;
  };
}

export interface AccumulationsBarChartProps {
  accumulations: Record<string, AccumulationsData["data"]>;
}

const formatPercentage = (accumulated: string, maximum: string) => {
  const acc = Number(accumulated);
  const max = Number(maximum);
  return max === 0 ? 0 : (acc / max) * 100;
};

const getAccumulationSections = (
  accumulations: Record<string, AccumulationsData["data"]>,
  prefix: "INN" | "OON",
) => [
  {
    title: "Individual Deductible",
    accumulated: accumulations[`${prefix} Deductible`]?.individual_accumulated,
    maximum: accumulations[`${prefix} Deductible`]?.individual_maximum,
  },
  {
    title: "Individual Maximum Out-of-Pocket",
    accumulated: accumulations[`${prefix} MOOP`]?.individual_accumulated,
    maximum: accumulations[`${prefix} MOOP`]?.individual_maximum,
  },
  {
    title: "Family Deductible",
    accumulated: accumulations[`${prefix} Deductible`]?.family_accumulated,
    maximum: accumulations[`${prefix} Deductible`]?.family_maximum,
  },
  {
    title: "Family Maximum Out-of-Pocket",
    accumulated: accumulations[`${prefix} MOOP`]?.family_accumulated,
    maximum: accumulations[`${prefix} MOOP`]?.family_maximum,
  },
];

const AccumulationSection: FC<{
  title: string;
  accumulated: string;
  maximum: string;
}> = ({ title, accumulated, maximum }) => {
  const percentage = formatPercentage(accumulated, maximum);
  const isComplete = accumulated === maximum;

  const safeFormatDollars = (value: string | number | null | undefined) => {
    const num = Number(value);
    return isNaN(num) ? "n/a" : formatDollars(num);
  };

  return (
    <Stack spacing="small">
      <Semibold style={{ fontSize: "16px" }}>{title}</Semibold>
      <Body>Member limit is {safeFormatDollars(maximum)}</Body>
      <div className={styles.AccumulationBar}>
        <div className={styles.Filled} style={{ width: `${percentage}%` }} />
        {isComplete && (
          <div className={styles.Check}>
            <Icon name="check-mark" color="white" size="small" />
          </div>
        )}
      </div>
      <Stack spacing="none">
        <Shelf>
          <Semibold>{safeFormatDollars(accumulated)}</Semibold>
          <Shelf.Spacer />
          <Semibold>{safeFormatDollars(Number(maximum) - Number(accumulated))}</Semibold>
        </Shelf>
        <Shelf>
          <Body>Spent</Body>
          <Shelf.Spacer />
          <Body>Remaining</Body>
        </Shelf>
      </Stack>
    </Stack>
  );
};

export const Accumulations: FC<AccumulationsBarChartProps> = ({ accumulations }) => {
  const [networkSelection, setNetworkSelection] = useState("inn");

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const toggleMenu = () => setMenuIsOpen((prev) => !prev);
  const closeMenu = () => setMenuIsOpen(false);

  const handleNetworkChange = (value: "inn" | "oon") => {
    setNetworkSelection(value);
    closeMenu();
  };

  const prefix = networkSelection === "inn" ? "INN" : "OON";
  const sections = getAccumulationSections(accumulations, prefix).filter(
    ({ accumulated, maximum }) => accumulated && maximum,
  );

  return (
    <Stack spacing="medium-large">
      <Stack spacing="none">
        <Stack alignment="left">
          <Button
            icon="chevron-down"
            iconPosition="right"
            appearance={Buttons.Functional.Tertiary.Small}
            onClick={toggleMenu}
          >
            {networkSelection === "inn" ? "In-Network" : "Out-of-Network"}
          </Button>
        </Stack>
        <Stack>
          <Menu visible={menuIsOpen} onClose={closeMenu}>
            <Menu.Group>
              <Menu.Item type="normal" onClick={() => handleNetworkChange("inn")}>
                In-Network
              </Menu.Item>
              <Menu.Item type="normal" onClick={() => handleNetworkChange("oon")}>
                Out-of-Network
              </Menu.Item>
            </Menu.Group>
            <Menu.Group>
              <Menu.Item onClick={closeMenu}>Close</Menu.Item>
            </Menu.Group>
          </Menu>
        </Stack>
      </Stack>
      {["inn", "oon"].includes(networkSelection) && (
        <>
          {sections.map(({ title, accumulated, maximum }) => (
            <AccumulationSection
              key={`${prefix}-${title}`}
              title={title}
              accumulated={accumulated}
              maximum={maximum}
            />
          ))}
        </>
      )}
    </Stack>
  );
};
